import React, { useEffect, useState } from 'react';
import { RouterProvider, useRevalidator } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import router from '@/router';
import ScreenOperator from '@/components/common/ScreenOperator';
import { useSessionStore } from '@/store';
import { env } from '@/constant/configure';
import delay from '@/utils/delay';
import { onLogoutBroadcast } from '@/utils/authBroadcast';
import { signOutSession } from '@/services/auth.service';
import { isSodomallPartnerDomain } from '@/utils/checkDomain';
import sessionStore from '@/store/modules/sessionStore';
import spaceUserV2Store from '@/store/modules/spaceUserV2Store';
import {
    announceEmbedExpired,
    announceEmbedLoading,
    announceEmbedPath,
    isAllowedEmbedPath,
    isEmbedded,
    menuPath,
    onEmbedGo,
} from '@/utils/embed';

if (env === 'prd') {
    const console: any = window.console || {};
    const methods = ['log', 'debug', 'warn', 'info'];
    methods.forEach((method) => {
        if (!!console[method]) console[method] = () => {};
    });
}

function App() {
    const { syncWithStorage } = useSessionStore();
    async function handleSession(ev: StorageEvent) {
        if (ev.key === 'flowing-manager-session') {
            const isRehydrated = await syncWithStorage(ev.newValue || '');
            if (isRehydrated)
                delay(300, () => {
                    router.revalidate();
                }).then((r) => r);
        }
    }

    useEffect(() => {
        // localStorage 변경 감지 (기존 로직)
        window.addEventListener('storage', handleSession);

        // BroadcastChannel로 로그아웃 메시지 감지 (다중 탭 동기화)
        const unsubscribe = onLogoutBroadcast(() => {
            // 다른 탭에서 로그아웃 시 현재 탭도 로그아웃 처리
            signOutSession().then(() => {
                // 소도몰 파트너 도메인인 경우 로그인 페이지로 이동
                if (isSodomallPartnerDomain()) {
                    window.location.href = '/login';
                } else {
                    window.location.reload();
                }
            });
        });

        /*
         * 글로벌 소듀 안에 끼워 넣었을 때 (src/utils/embed.ts).
         * 몰 관리(/mall/*) 밖으로 나가면 /embed/mall 로 되돌려 다시 연다.
         * 지금 주소는 부모 창에 알려 왼쪽 메뉴 강조를 맞춘다.
         */
        // 되돌리기가 짧은 사이에 되풀이되면(세션이 계속 튕기는 경우) 멈춘다. 멈추면 부모 창이 새로 연다
        let bounces: number[] = [];
        const guard = (pathname: string) => {
            if (!isEmbedded()) return;
            if (!isAllowedEmbedPath(pathname)) {
                const now = Date.now();
                bounces = bounces.filter((t) => now - t < 10000).concat(now);
                if (bounces.length > 3) {
                    announceEmbedExpired();
                    return;
                }
                router.navigate('/embed/mall', { replace: true });
                return;
            }
            announceEmbedPath(pathname);
        };
        guard(router.state.location.pathname);
        // 화면을 불러오는 동안은 부모 창이 로딩을 띄운다. 바뀔 때만 알린다
        let lastLoading = false;
        const unsubscribeRouter = router.subscribe((state) => {
            const nowLoading = state.navigation.state !== 'idle';
            if (isEmbedded() && nowLoading !== lastLoading) {
                lastLoading = nowLoading;
                announceEmbedLoading(nowLoading);
            }
            if (state.navigation.state === 'idle') guard(state.location.pathname);
        });
        const offGo = onEmbedGo((menu) => {
            if (!isEmbedded()) return;
            const { mallId } = sessionStore.getState();
            if (!mallId) {
                router.navigate(`/embed/mall?menu=${encodeURIComponent(menu)}`);
                return;
            }
            const isSodomall = spaceUserV2Store.getState().grade_name === '소도몰';
            router.navigate(menuPath(mallId, menu, isSodomall));
        });

        return () => {
            unsubscribeRouter();
            offGo();
            window.removeEventListener('storage', handleSession);
            unsubscribe();
        };
    }, []);

    return (
        <HelmetProvider>
            <div className="App">
                {/* 타이틀/favicon은 ManagerLayout에서 사용자 역할에 따라 동적으로 설정됨 */}
                <RouterProvider router={router} fallbackElement={<ScreenOperator />} />
            </div>
        </HelmetProvider>
    );
}

export default App;
